import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Send, Loader2, Plus, MessageSquare, Trash2, ArrowLeft, Bot, User, Menu, X
} from "lucide-react";
import { LogoIcon } from "@/components/LogoWithText";
import SEO from "@/components/SEO";
import ReactMarkdown from "react-markdown";

const API = process.env.REACT_APP_BACKEND_URL;

const AdvisorPage = () => {
  const [auth, setAuth] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");

  // Auto-login if admin credentials are stored
  useEffect(() => {
    const stored = sessionStorage.getItem("admin_auth");
    if (stored) {
      setAuth(stored);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    const encoded = btoa(`${loginForm.username}:${loginForm.password}`);
    sessionStorage.setItem("admin_auth", encoded);
    setAuth(encoded);
    setLoginError("");
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#02040a] flex items-center justify-center p-4">
        <SEO title="Asesor Inteligente - Global Metafora APP" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <LogoIcon size={48} />
            <h1 className="text-white font-bold text-2xl mt-4">Asesor Inteligente</h1>
            <p className="text-[#94a3b8] text-sm mt-2">Acceso exclusivo para administradores</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4 bg-[#0a0f1e] rounded-2xl p-6 border border-white/10">
            <input
              data-testid="advisor-login-user"
              type="text"
              placeholder="Usuario"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-[#111827] border border-white/10 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              required
            />
            <input
              data-testid="advisor-login-pass"
              type="password"
              placeholder="Contrasena"
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-[#111827] border border-white/10 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              required
            />
            {loginError && <p className="text-red-400 text-xs">{loginError}</p>}
            <button
              data-testid="advisor-login-btn"
              type="submit"
              className="w-full py-3 rounded-lg bg-[#3b82f6] text-white font-bold text-sm hover:bg-[#2563eb] transition-colors"
            >
              Acceder
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return <AdvisorChat auth={auth} onAuthError={() => { sessionStorage.removeItem("admin_auth"); setAuth(null); setLoginError("Credenciales incorrectas"); }} />;
};

const AdvisorChat = ({ auth, onAuthError }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const headers = { Authorization: `Basic ${auth}` };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/api/advisor/sessions`, { headers });
      setSessions(res.data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/api/advisor/sessions/${sessionId}`, { headers });
      setActiveSession(sessionId);
      setMessages(res.data.messages || []);
      setSidebarOpen(false);
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
    }
  };

  const newChat = () => {
    setActiveSession(null);
    setMessages([]);
    setSidebarOpen(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/advisor/sessions/${sessionId}`, { headers });
      setSessions(sessions.filter((s) => s.session_id !== sessionId));
      if (activeSession === sessionId) newChat();
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(
        `${API}/api/advisor/chat`,
        { message: userMsg, session_id: activeSession },
        { headers }
      );
      setActiveSession(res.data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.response }]);
      fetchSessions();
    } catch (err) {
      if (err.response?.status === 401) {
        onAuthError();
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Error al conectar con el asesor. Intenta de nuevo." },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-[#02040a] flex overflow-hidden">
      <SEO title="Asesor Inteligente - Global Metafora APP" />

      {/* Sidebar */}
      <AnimatePresence>
        {(sidebarOpen || window.innerWidth >= 768) && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="fixed md:relative z-30 w-72 h-full bg-[#0a0f1e] border-r border-white/5 flex flex-col"
          >
            {/* Sidebar header */}
            <div className="p-4 border-b border-white/5">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <LogoIcon size={28} />
                <span className="text-white font-bold text-sm">
                  GLOBAL <span className="text-[#3b82f6]">METAFORA</span>
                </span>
              </Link>
              <button
                data-testid="new-chat-btn"
                onClick={newChat}
                className="w-full py-2.5 rounded-lg border border-white/10 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
              >
                <Plus size={16} /> Nueva Consulta
              </button>
            </div>

            {/* Sessions list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {sessions.map((s) => (
                <div
                  key={s.session_id}
                  data-testid={`session-${s.session_id}`}
                  onClick={() => loadSession(s.session_id)}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer text-sm transition-colors ${
                    activeSession === s.session_id
                      ? "bg-[#3b82f6]/10 text-white"
                      : "text-[#94a3b8] hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <MessageSquare size={14} className="flex-shrink-0" />
                  <span className="truncate flex-1">{s.title || "Sin titulo"}</span>
                  <button
                    onClick={(e) => deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 text-[#64748b] hover:text-red-400 transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-[#475569] text-xs text-center py-8">
                  No hay consultas anteriores
                </p>
              )}
            </div>

            {/* Back link */}
            <div className="p-4 border-t border-white/5">
              <Link
                to="/admin"
                className="flex items-center gap-2 text-[#64748b] hover:text-white text-xs transition-colors"
              >
                <ArrowLeft size={14} /> Volver al Admin
              </Link>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 bg-[#02040a]">
          <button
            className="md:hidden text-[#94a3b8] hover:text-white"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center">
            <Bot size={18} className="text-[#3b82f6]" />
          </div>
          <div>
            <h2 className="text-white font-bold text-sm">Asesor Inteligente</h2>
            <p className="text-[#64748b] text-[10px]">GPT-5.2 &middot; Planificacion de Sistemas</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
          {messages.length === 0 && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 rounded-2xl bg-[#3b82f6]/10 flex items-center justify-center mx-auto mb-4">
                  <Bot size={32} className="text-[#3b82f6]" />
                </div>
                <h3 className="text-white font-bold text-xl mb-2">Hola! Soy tu Asesor</h3>
                <p className="text-[#94a3b8] text-sm mb-6">
                  Dime que sistema quieres crear y te dare un plan completo con todo lo que necesitas.
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    "Quiero crear un sistema de inventario",
                    "Necesito un sistema para una barberia",
                    "Como planeo un sistema de reservas?",
                  ].map((suggestion, i) => (
                    <button
                      key={i}
                      data-testid={`suggestion-${i}`}
                      onClick={() => {
                        setInput(suggestion);
                        inputRef.current?.focus();
                      }}
                      className="text-left px-4 py-3 rounded-lg border border-white/5 text-[#94a3b8] text-sm hover:bg-white/5 hover:text-white transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.role === "assistant" && (
                <div className="w-7 h-7 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot size={14} className="text-[#3b82f6]" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                  msg.role === "user"
                    ? "bg-[#3b82f6] text-white rounded-tr-sm"
                    : "bg-[#111827] text-[#e2e8f0] rounded-tl-sm border border-white/5"
                }`}
              >
                {msg.role === "assistant" ? (
                  <div className="prose prose-invert prose-sm max-w-none [&>ul]:space-y-1 [&>ol]:space-y-1 [&_li]:text-[#c8d0dc] [&_strong]:text-white [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_h3]:text-[#3b82f6] [&_code]:bg-black/30 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[#06b6d4] [&_pre]:bg-black/40 [&_pre]:rounded-lg [&_pre]:p-3">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  msg.content
                )}
              </div>
              {msg.role === "user" && (
                <div className="w-7 h-7 rounded-lg bg-[#10b981]/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <User size={14} className="text-[#10b981]" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center flex-shrink-0">
                <Bot size={14} className="text-[#3b82f6]" />
              </div>
              <div className="bg-[#111827] rounded-2xl rounded-tl-sm px-4 py-3 border border-white/5">
                <div className="flex gap-1.5">
                  <motion.div className="w-2 h-2 rounded-full bg-[#3b82f6]" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0 }} />
                  <motion.div className="w-2 h-2 rounded-full bg-[#3b82f6]" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0.2 }} />
                  <motion.div className="w-2 h-2 rounded-full bg-[#3b82f6]" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: 0.4 }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-white/5 bg-[#02040a]">
          <form onSubmit={sendMessage} className="flex gap-2">
            <input
              ref={inputRef}
              data-testid="advisor-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Describe el sistema que quieres crear..."
              className="flex-1 px-4 py-3 rounded-xl bg-[#111827] border border-white/10 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              disabled={loading}
            />
            <motion.button
              data-testid="advisor-send-btn"
              type="submit"
              disabled={loading || !input.trim()}
              className="px-4 rounded-xl bg-[#3b82f6] text-white flex items-center justify-center disabled:opacity-40"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </motion.button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdvisorPage;
